import { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import { Checkbox, Select, Spin, Tooltip } from 'antd';
import { CheckboxChangeEventTarget } from 'antd/lib/checkbox/Checkbox';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';

import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faPlane,
  faQuestion,
  faShieldHeart,
  faSuitcase,
  faUsd,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { USA_STATES } from '@constants';
import { IInsuranceState, InsuranceSelection, insuranceActions, refreshInsuranceQuote } from '@store/slices';
import { ILoginState, IMenuState } from '@share/store/slices';
import { RootState, Toaster } from '@share/utils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AIGLogo from '@assets/images/aig-logo.png';

import './style.scss';

const COUNTRY_CODE = 'COUNTRY_CODE';
const STATE_CODE = 'STATE_CODE';
const PRODUCT_CODE = 'PRODUCT_CODE';

const POLICY_INSURANCE_URL = `https://webservices.travelguard.com/Product/FileRetrieval.aspx?CountryCode=${COUNTRY_CODE}&StateCode=${STATE_CODE}&ProductCode=${PRODUCT_CODE}&PlanCode=NW21&FileType=PROD_PLAN_GM`;
const DISCALIMER_URL = 'https://www.travelguard.com/legal/disclaimer';

interface IMapStateToProps {
  insuranceStore: IInsuranceState;
  menuStore: IMenuState;
  loginStore: ILoginState;
}

interface IMapDispatchToProps {
  setSelection: (selection: InsuranceSelection | null) => void;
  refreshInsuranceQuote: (state: string, priceChangeHandler?: () => void) => void;
}

interface IProps extends IMapStateToProps, IMapDispatchToProps, WrappedComponentProps {
  textOnly?: boolean;
  /**
   * Read-only presentation for the post-booking cross-sell: no checkboxes, no decline
   * option, and the price line reads as an offer rather than a selection.
   */
  crossSell?: boolean;

  onChange?: () => void;
  /** When card billing state is not available (full Stripe / express wallet), user selects US state for quote only. */
  showQuoteResidencyStateDropdown?: boolean;
  /**
   * Force-full Stripe insurance flow: render an "Insurance" header with a residency State dropdown
   * (insurance is US-only, so there is no country selector — picking a state implies the USA and the
   * country is set to 'US' for the caller). The offer box is always visible: until a quote resolves
   * the "Yes" option is greyed out and a banner above the options explains what's missing, while
   * "No" stays selectable so the guest can decline without picking a state.
   */
  showResidencyCountryState?: boolean;
  /** Force-full flow: residency selection is owned by the parent so it survives the details↔payment
   *  phase switch (this component unmounts in the payment phase) regardless of quote availability. */
  residencyCountry?: string;
  residencyState?: string;
  onResidencyCountryChange?: (value: string) => void;
  onResidencyStateChange?: (value: string) => void;
  /**
   * Force-full insurance flow: reports whether the guest still has to act before checkout can
   * continue — i.e. they have neither declined nor accepted a resolved quote. Lets the parent
   * disable "go to payments" accordingly.
   */
  onResidencyStepChange?: (blocked: boolean) => void;
  /** Force-full flow: when true, an empty state selector is shown in an error (red) state — set when
   *  the guest clicks "View Fields" so the residency selector flags as required like other fields. */
  highlightErrors?: boolean;
  /** Condos full-Stripe: the residency is mirrored from the primary-address section, so the selector
   *  is read-only while that address is outside the US (nothing to pick — insurance is US-only).
   *  A US address leaves it editable so the guest can override the mirrored state, same as hotels,
   *  which have no primary-address section at all. */
  disableResidencySelectors?: boolean;
}

function InsuranceComponent(props: IProps) {
  const {
    textOnly,
    crossSell,
    insuranceStore,
    loginStore,
    menuStore,
    intl,
    setSelection,
    onChange,
    refreshInsuranceQuote,
    showQuoteResidencyStateDropdown,
    showResidencyCountryState,
    residencyCountry = '',
    residencyState = '',
    onResidencyCountryChange,
    onResidencyStateChange,
    onResidencyStepChange,
    highlightErrors,
    disableResidencySelectors,
  } = props;
  const { insurance, travelersRequest, loading, error, selection } = insuranceStore;
  const { account } = loginStore;
  const { items } = menuStore;

  // Force-full mode: report to the parent whether the guest still has to complete the insurance step
  // so it can gate the "go to payments" button.
  useEffect(() => {
    if (!showResidencyCountryState || !onResidencyStepChange) {
      return;
    }
    // Declining never needs a residency: the guest can opt out straight away. Accepting requires a
    // resolved quote, which in turn requires the state. And we only insist on an answer while
    // insurance could still be sold — a location we can't quote for doesn't hold up checkout.
    const isUS = residencyCountry === 'US';
    const offerAvailable = isUS && !!residencyState && !!insurance && isEmpty(error) && !loading;
    const offerImpossible =
      (!!residencyCountry && !isUS) ||
      (isUS && !!residencyState && !loading && (!insurance || !isEmpty(error)));
    const blocked =
      selection !== InsuranceSelection.NO &&
      !(offerAvailable && selection === InsuranceSelection.YES) &&
      !offerImpossible;
    onResidencyStepChange(blocked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residencyCountry, residencyState, insurance, error, loading, selection]);

  // Force-full mode: a state whose quote comes back empty leaves nothing to accept, so record the
  // decline for the guest rather than waiting on an answer they can't give. Driven by the quote
  // settling (loading true → false) so a state that was never quoted is left untouched.
  const quoteWasLoadingRef = useRef(false);
  useEffect(() => {
    const quoteSettled = quoteWasLoadingRef.current && !loading;
    quoteWasLoadingRef.current = loading;

    if (!showResidencyCountryState || !quoteSettled) {
      return;
    }
    if (!insurance || !isEmpty(error)) {
      setSelection(InsuranceSelection.NO);
      onChange?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!account?.enableInsurance || items?.isRemoveInsurance) {
    return null;
  }

  /* Read-only presentations of this panel: the reservations modal (textOnly) and the
     post-booking cross-sell, which shows the offer without any way to act on it. */
  const isReadOnly = textOnly || crossSell;

  const handleToaster = (message: string, isWarning?: boolean) => {
    if (isWarning) {
      Toaster.warning(intl.formatMessage({ id: message }), {
        position: 'bottom-center',
        icon: false,
        hideProgressBar: true,
      });
    } else {
      Toaster.info(intl.formatMessage({ id: message }), {
        position: 'bottom-center',
        icon: false,
        hideProgressBar: true,
      });
    }
  };

  const policyInsuranceUrl = POLICY_INSURANCE_URL
    .replace(COUNTRY_CODE, travelersRequest?.residency?.isoCountry as string)
    .replace(STATE_CODE, travelersRequest?.residency?.isoStateOrProvince as string)
    .replace(PRODUCT_CODE, insurance?.productDetails?.productCode as string);

  const handleYesSelection = (e: { target: CheckboxChangeEventTarget }) => {
    const selection: any = InsuranceSelection.YES;
    setSelection(selection);

    if (e.target.checked) {
      handleToaster('insurance.policy.protected', false);
    }
  };

  const handleNoSelection = (e: { target: CheckboxChangeEventTarget }) => {
    const selection: any = InsuranceSelection.NO;
    setSelection(selection);

    if (onChange) {
      onChange();
    }

    if (e.target.checked) {
      handleToaster('insurance.policy.un_protected', true);
    }
  };

  const quoteResidencyState =
    travelersRequest?.residency?.isoStateOrProvince || 'FL';

  const onQuoteResidencyStateChange = (value: string) => {
    refreshInsuranceQuote(value, () => handleToaster('insurance.policy.state_change'));
  };

  // ── Force-full residency selector ───────────────────────────────────────────────────────────────
  // The selected value is owned by the parent (props) so it persists across the details↔payment
  // phase switch even when no quote is produced (non-US address, or a US state without insurance).
  // Only the state is asked for in the force-full flow: insurance is US-only, so picking a state
  // implies the country and we set it to US ourselves (the parent keeps owning the value).
  const handleResidencyState = (value: string) => {
    const hadState = !!residencyState && residencyCountry === 'US';

    if (residencyCountry !== 'US') {
      onResidencyCountryChange?.('US');
    }
    onResidencyStateChange?.(value);

    // The answer belonged to the previous state's quote — a new state means a new price, so the
    // guest has to accept or decline again.
    if (selection !== null && selection !== undefined) {
      setSelection(null);
      onChange?.();
    }

    refreshInsuranceQuote(
      value,
      hadState ? () => handleToaster('insurance.policy.state_change') : undefined,
    );
  };

  // ── Force-full hybrid state ─────────────────────────────────────────────────────────────────────
  // The offer box is always rendered in force-full mode. Until a quote exists the "Yes" option is
  // greyed out and a banner above the options explains what's missing; "No" always stays available
  // so the guest can decline outright without picking a state.
  const isUSResidency = residencyCountry === 'US';
  const quotePending = !!showResidencyCountryState && isUSResidency && !!residencyState && loading;
  const offerAvailable =
    !showResidencyCountryState ||
    (isUSResidency && !!residencyState && !!insurance && isEmpty(error) && !loading);
  const yesDisabled = !!showResidencyCountryState && !offerAvailable;
  // Banner shown with the state selector: the invite sits above it, the outcome of the pick
  // (loading → available / not available) below it, just before the checkboxes.
  const banner: { tone: 'info' | 'success' | 'warning'; id: string } | null =
    !showResidencyCountryState
      ? null
      : quotePending
        ? { tone: 'info', id: 'insurance.quote.loading' }
        : offerAvailable
          ? { tone: 'success', id: 'insurance.protect.available' }
          : residencyCountry && !isUSResidency
            ? { tone: 'warning', id: 'insurance.not.available.location' }
            : !residencyState
              ? { tone: 'info', id: 'insurance.protect.select.state' }
              : { tone: 'warning', id: 'insurance.not.available.location' };
  const isInviteBanner = banner?.id === 'insurance.protect.select.state';

  const renderBanner = (b: { tone: 'info' | 'success' | 'warning'; id: string }) => (
    <div className={`insurance-banner insurance-banner--${b.tone}`}>
      <div className="insurance-banner__icon">
        {quotePending ? (
          <Spin size="small" />
        ) : (
          <FontAwesomeIcon
            icon={
              b.tone === 'success'
                ? faCircleCheck
                : b.tone === 'warning'
                  ? faCircleExclamation
                  : faShieldHeart
            }
          />
        )}
      </div>
      <div className="insurance-banner__text">
        <FormattedMessage id={b.id} />
      </div>
    </div>
  );

  // The residency state rates and issues the policy; the billing address on the card is a separate
  // thing and the two are allowed to differ (a guest can live in FL and pay with a card billed to a
  // NY office). The dropdown is seeded from the billing address, so say plainly that it can be
  // changed. Shared by every engine's residency dropdown.
  const residencyLabel = (
    <span className="insurance-residency__label">
      <FormattedMessage id="insurance.residence.label" />
      <Tooltip title={intl.formatMessage({ id: 'insurance.residence.tooltip' })}>
        <span className="insurance-residency__info" tabIndex={0}>
          <FontAwesomeIcon icon={faCircleInfo} />
        </span>
      </Tooltip>
    </span>
  );

  // Force-full: the residency state is asked for inside the offer box, between the invite and the
  // options. Insurance is US-only, so picking a state is what grants access to the offer and
  // implies the United States (the country is set to 'US' for the caller).
  const residencySelector = (
    <div className="insurance-residency__field">
      {residencyLabel}
      <Select
        showSearch
        className="insurance-residency__select"
        popupClassName="insurance-residency-select"
        disabled={loading || disableResidencySelectors}
        status={highlightErrors && !residencyState ? 'error' : undefined}
        value={isUSResidency && residencyState ? residencyState : undefined}
        placeholder={intl.formatMessage({ id: 'insurance.residence.placeholder' })}
        onChange={handleResidencyState}
        filterOption={(inputValue: string, option: any) => {
          const inputLower = inputValue.toLowerCase();
          return (
            option.children.toString().toLowerCase().includes(inputLower) ||
            option.value.toString().toLowerCase().includes(inputLower)
          );
        }}
      >
        {USA_STATES.map(({ postalCode, name }) => (
          <Select.Option key={postalCode} value={postalCode}>
            {name}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  // Without a quote the policy URL is built out of undefined values — keep the wording but drop the
  // link so the guest can't open a broken document.
  const hasPolicyDocument =
    !!insurance?.productDetails?.productCode && !!travelersRequest?.residency?.isoCountry;

  const policyLink = (id: string) =>
    hasPolicyDocument ? (
      <a target="_blank" href={policyInsuranceUrl}>
        <FormattedMessage id={id} />
      </a>
    ) : (
      <FormattedMessage id={id} />
    );

  const offerBody = (
    <>
      <div className="insurance-container__header">
        <div className="header-title">Travel Guard &#174; <FormattedMessage id="insurance.travel.insurance" /></div>
        <div className="header-logo"><img src={AIGLogo} width="60" alt="aig_logo" /></div>
      </div>
      <div className="insurance-container__content">
        <div className="title"><FormattedMessage id="insurance.cover.your.trip" /></div>
        <div className="sub-title"><FormattedMessage id="insurance.ignore.unexpected" /></div>
        {showQuoteResidencyStateDropdown ? (
          <div className="quote-residency-state">
            {residencyLabel}
            <Select
              showSearch
              className="quote-residency-state__select"
              popupClassName="quote-residency-state-select"
              disabled={loading}
              value={quoteResidencyState}
              placeholder={intl.formatMessage({ id: 'state' })}
              onChange={onQuoteResidencyStateChange}
              filterOption={(inputValue: string, option: any) => {
                const inputLower = inputValue.toLowerCase();
                return (
                  option.children.toString().toLowerCase().includes(inputLower) ||
                  option.value.toString().toLowerCase().includes(inputLower)
                );
              }}
            >
              {USA_STATES.map(({ postalCode, name }) => (
                <Select.Option key={postalCode} value={postalCode}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </div>
        ) : null}
        <div className="container-fluid" style={{ marginBottom: '10px' }}>
          <div className="row">
            <div className="col-md-6 insurance-option"><div className="icon-insurance"><FontAwesomeIcon icon={faUser} /></div><FormattedMessage id="insurance.family.member" /></div>
            <div className="col-md-6 insurance-option"><div className="icon-insurance"><FontAwesomeIcon icon={faPlane} /></div><FormattedMessage id="insurance.miss.connection" /></div>
            <div className="col-md-6 insurance-option"><div className="icon-insurance"><FontAwesomeIcon icon={faQuestion} /></div><FormattedMessage id="insurance.lose.passport" /></div>
            <div className="col-md-6 insurance-option"><div className="icon-insurance"><FontAwesomeIcon icon={faPlane} /></div><FormattedMessage id="insurance.trip.delayed" /></div>
            <div className="col-md-6 insurance-option"><div className="icon-insurance"><FontAwesomeIcon icon={faSuitcase} /></div><FormattedMessage id="insurance.baggage.lost" />.</div>
            <div className="col-md-6 insurance-option"><div className="icon-insurance"><FontAwesomeIcon icon={faUsd} /></div><FormattedMessage id="insurance.last.minute.accomodation" /></div>
          </div>
        </div>
        <div style={{ marginBottom: '20px' }}><FormattedMessage id="insurance.message" /> {policyLink('insurance.message.click')}</div>
        {showResidencyCountryState ? (
          <div className="insurance-residency__inline">
            {banner && isInviteBanner ? renderBanner(banner) : null}
            {residencySelector}
            {banner && !isInviteBanner ? renderBanner(banner) : null}
          </div>
        ) : null}

        <div className={`yes-selection ${isReadOnly ? 'text-only' : ''} ${yesDisabled ? 'disabled' : ''}`}>
          {!isReadOnly ? (
            <Checkbox
              disabled={yesDisabled}
              checked={selection === InsuranceSelection.YES}
              onChange={handleYesSelection}
            />) : null}

          <label className="selection-text">
            {offerAvailable ? (
              <><FormattedMessage id={crossSell ? 'insurance.crossSell.price' : 'insurance.yes'} /> ${insurance?.productDetails?.price} </>
            ) : (
              <><FormattedMessage id="insurance.yes.no_price" /> </>
            )}
            <small>(<FormattedMessage id="insurance.only.us" />)</small>
          </label>
        </div>
        <div className={`yes-selection-text ${isReadOnly ? 'text-only' : ''} ${yesDisabled ? 'disabled' : ''}`}>
          <FormattedMessage
            id="insurance.acknowledge"
            values={{
              ap: (_msg: string) => policyLink('insurance.policy.insurance'),
              ai: (_msg: string) => {
                return (
                  <a target="_blank" href={DISCALIMER_URL}>
                    <FormattedMessage id="insurance.important.disclousures" />
                  </a>);
              },
            }}
          />
        </div>

        {!isReadOnly ? (
          <div className="no-selection">
            <Checkbox
              checked={selection === InsuranceSelection.NO}
              onChange={handleNoSelection}
            />
            {/* Force-full: declining is also the way out for guests who live outside the US, so the
                label has to cover both readings. */}
            <label className="selection-text">
              <FormattedMessage
                id={showResidencyCountryState ? 'insurance.no.non_resident' : 'insurance.no'}
              />
            </label>
          </div>) : null}

        <div className="coverage-message"><FormattedMessage id="insurance.coverage" /> <a href={DISCALIMER_URL} target="_blank"><FormattedMessage id="insurance.coverage.click" /></a></div>
      </div>
    </>
  );

  if (showResidencyCountryState) {
    return (
      <div className="insurance-residency">
        <div className="insurance-residency__header">
          <h4 className="insurance-residency__title secondary-font">
            <FormattedMessage id="insurance.title" />
          </h4>
        </div>

        <div className={`insurance-container ${yesDisabled ? 'insurance-container--pending' : ''}`}>
          {offerBody}
        </div>
      </div>
    );
  }

  if (loading || !isEmpty(error) || !insurance) {
    return null;
  }

  return <div className="insurance-container">{offerBody}</div>;
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    insuranceStore: state.insuranceStore,
    menuStore: state.navigationMenuStore,
    loginStore: state.loginStore,
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  setSelection: insuranceActions.setSelection,
  refreshInsuranceQuote,
};

const component = injectIntl(InsuranceComponent);

export const Insurance = connect(mapStateToProps, mapDispatchToProps)(component);
